import React from 'react';
import {
  Image,
  Stack,
  Paper,
  Group,
  Box,
  UnstyledButton,
  ActionIcon,
  Overlay,
  Transition,
  rem,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconMaximize,
} from '@tabler/icons-react';

export default function ProductDetailImageGallery({
  displayImages,
  selectedImageIndex,
  onSelectImage,
  productName,
  isSoldOut,
  previewMode = false,
  onOpenLightbox,
  onHoverChange,
  hovered,
}) {
  const mainImage =
    displayImages[selectedImageIndex] ||
    displayImages[0] ||
    'https://placehold.co/600x600?text=Yogi+Foods';

  const handlePrev = () =>
    onSelectImage(
      (displayImages.length + selectedImageIndex - 1) % displayImages.length
    );
  const handleNext = () =>
    onSelectImage((selectedImageIndex + 1) % displayImages.length);

  return (
    <Stack gap="md">
      {/* Main Image */}
      <Paper radius="lg" withBorder={false} p="xl" bg="white" pos="relative">
        <UnstyledButton
          onClick={previewMode ? undefined : onOpenLightbox}
          onMouseEnter={previewMode ? undefined : () => onHoverChange?.(true)}
          onMouseLeave={previewMode ? undefined : () => onHoverChange?.(false)}
          style={{ display: 'block', width: '100%', cursor: previewMode ? 'default' : 'zoom-in', padding: 0 }}
          aria-label={previewMode ? undefined : 'Open image in lightbox'}
          tabIndex={previewMode ? -1 : undefined}
        >
          <Image
            src={mainImage}
            alt={productName}
            fallbackSrc="https://placehold.co/600x600?text=Yogi+Foods"
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              filter: isSoldOut ? 'grayscale(1)' : 'none',
            }}
          />
          {!previewMode && !isSoldOut && (
            <Transition mounted={hovered} transition="fade" duration={200}>
              {(hoverStyles) => (
                <Box style={hoverStyles}>
                  <Overlay color="#000" opacity={0.25} zIndex={1} />
                  <Box
                    pos="absolute"
                    style={{
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    <IconMaximize color="white" size={28} stroke={1.5} aria-hidden="true" />
                  </Box>
                </Box>
              )}
            </Transition>
          )}
          {isSoldOut && (
            <Overlay color="#000" opacity={0.25} zIndex={1} />
          )}
        </UnstyledButton>

        {displayImages.length > 1 && (
          <>
            <ActionIcon
              variant="white"
              color="gray"
              size="lg"
              radius="xl"
              onClick={handlePrev}
              aria-label="Previous product image"
              tabIndex={previewMode ? -1 : undefined}
              style={{
                position: 'absolute',
                left: rem(16),
                top: '50%',
                transform: 'translateY(-50%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <IconChevronLeft size={20} aria-hidden="true" />
            </ActionIcon>
            <ActionIcon
              variant="white"
              color="gray"
              size="lg"
              radius="xl"
              onClick={handleNext}
              aria-label="Next product image"
              tabIndex={previewMode ? -1 : undefined}
              style={{
                position: 'absolute',
                right: rem(16),
                top: '50%',
                transform: 'translateY(-50%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <IconChevronRight size={20} aria-hidden="true" />
            </ActionIcon>
          </>
        )}
      </Paper>

      {/* Thumbnails */}
      {displayImages.length > 1 && (
        <Group gap="xs">
          {displayImages.map((img, index) => (
            <UnstyledButton
              key={index}
              onClick={() => onSelectImage(index)}
              tabIndex={previewMode ? -1 : undefined}
              style={{
                border:
                  selectedImageIndex === index
                    ? '2px solid var(--mantine-color-blue-6)'
                    : '2px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-sm)',
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                padding: 0,
                lineHeight: 0,
              }}
            >
              <Image
                src={img}
                alt={selectedImageIndex === index ? `${productName} ${index + 1} (selected)` : `${productName} ${index + 1}`}
                h={64}
                w={64}
                fit="cover"
                fallbackSrc="https://placehold.co/64x64?text=No+Image"
              />
            </UnstyledButton>
          ))}
        </Group>
      )}
    </Stack>
  );
}
